import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Circle, ChevronDown, ChevronRight } from 'lucide-react';
import { Skeleton } from '../../../shared/components/Skeleton';
import { Alert } from '../../../shared/components/Alert';
import { listarUsuarios } from '../../usuarios/api';
import { useHistorico } from '../hooks';
import type { OpportunityHistoryEntry } from '../api';
import { formatDateTimeBR } from '../utils/format';

const ACTION_LABEL: Record<string, string> = {
  'opportunity.created': 'Criada',
  'opportunity.updated': 'Atualizada',
  'opportunity.draft_promoted': 'Rascunho promovido',
  'opportunity.stage_changed': 'Etapa alterada',
  'opportunity.probability_changed': 'Probabilidade alterada',
  'opportunity.value_changed': 'Valor alterado',
  'opportunity.responsible_transferred': 'Responsável transferido',
  'opportunity.suspended': 'Suspensa',
  'opportunity.reactivated': 'Reativada',
  'opportunity.won': 'Ganha',
  'opportunity.lost': 'Perdida',
  'opportunity.cancelled': 'Cancelada',
  'opportunity.converted_to_proposal': 'Convertida em proposta',
  'opportunity.interaction_added': 'Interação registrada',
  'opportunity.interaction_removed': 'Interação removida',
  'opportunity.attachment_added': 'Anexo enviado',
  'opportunity.attachment_removed': 'Anexo removido',
  'opportunity.competitor_added': 'Concorrente vinculado',
  'opportunity.competitor_removed': 'Concorrente removido',
  'opportunity.competitor_winner_set': 'Concorrente vencedor definido',
  'opportunity.participant_added': 'Participante adicionado',
  'opportunity.participant_removed': 'Participante removido',
  'opportunity.margin_approved': 'Margem aprovada',
  'opportunity.margin_approval_invalidated': 'Aprovação de margem invalidada',
};

const ACTION_COLOR: Record<string, string> = {
  'opportunity.won': 'text-green-700',
  'opportunity.lost': 'text-red-700',
  'opportunity.cancelled': 'text-gray-500',
  'opportunity.suspended': 'text-amber-700',
  'opportunity.reactivated': 'text-blue-700',
  'opportunity.margin_approval_invalidated': 'text-amber-700',
};

interface Props {
  opportunityId: string;
}

/**
 * Aba Histórico do detalhe da oportunidade (US-COM-004 §Auditoria e
 * Histórico, task 16.1). Consome `opportunity_change_history` via
 * `GET /opportunities/:id/history` e renderiza eventos ordenados do
 * mais recente para o mais antigo.
 *
 * Cada linha mostra: ação (em português), autor, timestamp, campos
 * alterados (badges) e — via toggle "Detalhes" — o diff completo
 * old_value/new_value em JSON monospace. `reason` aparece em blockquote
 * quando presente (retrocessos, cancelamentos, aprovações).
 */
export const OpportunityTimeline: React.FC<Props> = ({ opportunityId }) => {
  const { data, isLoading, isError, error } = useHistorico(opportunityId, { limit: 100 });

  const { data: usuariosResp } = useQuery({
    queryKey: ['picker', 'usuarios'],
    queryFn: () => listarUsuarios({ limit: 200 }),
    staleTime: 60 * 1000,
  });
  const userNameById = useMemo(() => {
    const m = new Map<string, string>();
    (usuariosResp?.data ?? []).forEach((u) => m.set(u.id, u.name));
    return m;
  }, [usuariosResp]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <Alert variant="error">
        Erro ao carregar histórico: {(error as Error)?.message ?? 'Falha desconhecida.'}
      </Alert>
    );
  }
  if (!data || data.data.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        Nenhum evento registrado nesta oportunidade ainda.
      </p>
    );
  }

  return (
    <div className="relative">
      {/* Linha vertical à esquerda dos marcadores. */}
      <div className="absolute left-2 top-2 bottom-2 w-px bg-gray-200" aria-hidden />
      <ul className="space-y-3">
        {data.data.map((h) => (
          <TimelineEntry key={h.id} entry={h} userNameById={userNameById} />
        ))}
      </ul>
      {data.pagination.count > data.data.length && (
        <p className="mt-3 text-center text-xs text-gray-500">
          Mostrando {data.data.length} de {data.pagination.count} eventos. Paginação
          por scroll infinito entra em fatia posterior.
        </p>
      )}
    </div>
  );
};

const TimelineEntry: React.FC<{
  entry: OpportunityHistoryEntry;
  userNameById: Map<string, string>;
}> = ({ entry, userNameById }) => {
  const [showDiff, setShowDiff] = useState(false);
  const label = useMemo(
    () => ACTION_LABEL[entry.action] ?? entry.action,
    [entry.action],
  );
  const colorClass = useMemo(
    () => ACTION_COLOR[entry.action] ?? 'text-gray-800',
    [entry.action],
  );

  return (
    <li className="relative pl-8">
      <span className="absolute left-1 top-2" aria-hidden>
        <Circle className={`h-3 w-3 fill-current ${colorClass}`} />
      </span>
      <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className={`text-sm font-semibold ${colorClass}`}>{label}</span>
          <span className="text-xs text-gray-500">{formatDateTimeBR(entry.createdAt)}</span>
        </div>
        <div className="mt-0.5 text-xs text-gray-500">
          {entry.userId ? (
            <>
              por{' '}
              <span className="font-medium text-gray-600">
                {userNameById.get(entry.userId) ?? 'usuário desconhecido'}
              </span>
            </>
          ) : (
            <>por sistema</>
          )}
        </div>
        {entry.reason && (
          <blockquote className="mt-2 border-l-2 border-gray-200 pl-3 text-xs italic text-gray-600">
            {entry.reason}
          </blockquote>
        )}
        {entry.changedFields.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {entry.changedFields.map((f) => (
              <span
                key={f}
                className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-700"
              >
                {f}
              </span>
            ))}
          </div>
        )}
        {(entry.oldValue || entry.newValue) && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setShowDiff((v) => !v)}
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
            >
              {showDiff ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              {showDiff ? 'Ocultar detalhes' : 'Ver detalhes'}
            </button>
            {showDiff && (
              <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                {entry.oldValue && (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Antes
                    </div>
                    <pre className="overflow-x-auto rounded bg-red-50 p-2 text-[10px] text-red-900">
                      {JSON.stringify(entry.oldValue, null, 2)}
                    </pre>
                  </div>
                )}
                {entry.newValue && (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Depois
                    </div>
                    <pre className="overflow-x-auto rounded bg-green-50 p-2 text-[10px] text-green-900">
                      {JSON.stringify(entry.newValue, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  );
};
